import ClockIcon from 'components/ReusableComponents/Icons/Clock'
import CopyIcon from 'components/ReusableComponents/Icons/CopyIcon'
import { Cell, Row } from 'components/ReusableComponents/Table'
import RemindIssueLaterDialog from 'features/Hotels/HotelCard/RemindIssueLaterDialog'
import useBrand from 'hooks/useBrand'
import useCopyText from 'hooks/useCopyText'
import HotelIcon from 'img/hotels.svg'
import moment from 'moment'
import { useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useHistory } from 'react-router'
import { useToasts } from 'react-toast-notifications'
import subsidiariesApi from 'services/api/subsidiaries'

import { selectCurrentHotelName } from '../../../accessors'
import {
  generateShortNameAndBrand,
  postAndSelectSubsidiary,
  savePrevPath
} from '../../../actions'
import { saveSubsidiary } from '../../../utils/helpers'
import styles from './HotelCard.module.scss'
import { getBrokenCtaText, getIssueText, getStatusText } from './status'

/**
 *
 * @typedef Hotel
 * @property {string} hotelName
 * @property {string} bookingEngine
 * @property {string} chainCode
 * @property {string} parentChainCode
 * @property {string} textMarketingUrlKey
 * @property {string} addedOn
 * @property {number} addedBy
 * @property {string} updatedOn
 * @property {number} updatedBy
 * @property {string} id
 * @property {number} numberOfRooms
 * @property {string} pms
 * @property {boolean} isMissingSomeInformation
 * @property {number} displayId
 * @property {boolean} isDeleted
 * @property {number} status
 * @property {object[]} subsidiaryPmsUsers
 * @property {number} subsidiaryPmsUsers.id
 * @property {string} subsidiaryPmsUsers.subsidiaryId
 * @property {string} subsidiaryPmsUsers.email
 * @property {string} subsidiaryPmsUsers.firstName
 * @property {string} subsidiaryPmsUsers.lastName
 * @property {string} subsidiaryPmsUsers.birthDate
 * @property {string} subsidiaryPmsUsers.addedOn
 * @property {number} subsidiaryPmsUsers.addedBy
 * @property {string} merchantName
 * @property {boolean} inviteBackIsDisabled
 * @property {object[]} inviteBlockOn
 * @property {string} inviteBlockOn.addedBy
 * @property {string} inviteBlockOn.addedOn
 * @property {string} inviteBlockOn.reason
 * @property {boolean} pmsSyncIsConnected
 * @property {boolean} isServiceStarted
 * @property {boolean} isCampaignsEnabled
 * @property {boolean} isTcrActivated
 * @property {boolean} isPhoneEnabled
 * @property {object[]} tags
 * @property {number} tags.id
 * @property {string} tags.name
 * @property {string} tags.addedBy
 * @property {string} tags.addedOn
 * @property {string} processKind
 *
 * @typedef {object} Props
 * @property {Hotel} hotel
 * @property {boolean} isHealthyHotel
 * @property {boolean} isOnboarding
 * @property {string} category
 * @property {function} refetchData
 */

/**
 *
 * @type {React.FC<Props>}
 * @returns {React.ReactElement}
 */
export const HotelCard = props => {
  const { hotel, isHealthyHotel, isOnboarding, index } = props
  const dispatch = useDispatch()
  const history = useHistory()
  const copy = useCopyText()
  const currentHotelName = useSelector(selectCurrentHotelName)
  const { logoUrl, colors } = useBrand({
    subsidiaryShortLinkToken: hotel.textMarketingUrlKey,
    parentChainCode: hotel.parentChainCode
  })

  const [isRemindLaterDialogVisible, setIsRemindLaterDialogVisible] = useState(false)

  const [isPopupVisible, setIsPopupVisible] = useState(false)

  const { addToast } = useToasts()

  const handleMouseEnter = () => {
    setIsPopupVisible(true)
  }

  const handleMouseLeave = () => {
    setIsPopupVisible(false)
  }

  function handleHotelClick() {
    if (currentHotelName !== hotel.hotelName) {
      dispatch(savePrevPath('/hotelDetails'))
      saveSubsidiary({
        subsidiaryId: hotel.id
      })
      dispatch(postAndSelectSubsidiary(hotel.id))
    } else {
      history.push('/hotelDetails')
    }
  }

  const pmsUser = hotel.subsidiaryPmsUsers?.[0]
  const { brand, shortName } = generateShortNameAndBrand(hotel.hotelName)

  const tags = hotel.tags?.slice(0, 2).map(tag => tag.name)

  return (
    <div onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave}>
      {isRemindLaterDialogVisible && (
        <RemindIssueLaterDialog
          onClose={() => setIsRemindLaterDialogVisible(false)}
          hotelName={hotel.hotelName}
          prevRemindLaterReason={hotel?.remindLater?.reason ?? ''}
          prevRemindLaterAfter={hotel?.remindLater?.afterOn ?? ''}
          prevAddedBy={hotel?.remindLater?.updatedBy ?? ''}
          prevAddedOn={hotel?.remindLater?.updatedOn ?? ''}
          onSave={({ remindLaterReason, remindLaterAfter }) => {
            subsidiariesApi
              .remindLater({
                subsidiaryId: hotel.id,
                remindLaterReason,
                remindLaterAfter
              })
              .then(() => {
                addToast('Reminder saved', { appearance: 'success' })
              })
              .catch(() => {
                addToast('Failed to save reminder', { appearance: 'error' })
              })
              .then(() => props.refetchData())
            setIsRemindLaterDialogVisible(false)
          }}
        />
      )}
      {isPopupVisible && (
        <div className={styles.popup}>
          <div className={styles.logoWrapper} style={{ backgroundColor: colors.primary }}>
            <img
              className={styles.logo}
              src={logoUrl}
              onError={e => {
                e.currentTarget.onerror = null
                e.currentTarget.src = HotelIcon
              }}
              alt="hotel logo"
            />
          </div>

          <div className={styles.infoCol}>
            <div className={styles.info}>
              <div>
                <span className={styles.name}>BE</span>
                <span className={styles.value}>{hotel.bookingEngine ?? 'N/A'}</span>
              </div>
              <div>
                <span className={styles.name}>PMS</span>
                <span className={styles.value}>{hotel.pms ?? 'N/A'}</span>
              </div>
            </div>

            <div className={styles.details}>
              {hotel.textMarketingUrlKey && (
                <div onClick={() => copy(hotel.textMarketingUrlKey)}>
                  <span className={styles.value}>{hotel.textMarketingUrlKey}</span>
                  <CopyIcon width={12} />
                </div>
              )}
              {pmsUser?.email && (
                <div onClick={() => copy(pmsUser.email)}>
                  <span className={styles.value}>{pmsUser.email}</span>
                  <CopyIcon width={12} />
                </div>
              )}
              {pmsUser?.subsidiaryId && (
                <div onClick={() => copy(pmsUser.subsidiaryId)}>
                  <span className={styles.value}>{pmsUser.subsidiaryId}</span>
                  <CopyIcon width={12} />
                </div>
              )}
            </div>
          </div>
        </div>
      )}
      <div className={styles.index}>{index}</div>
      <Row className={styles.row}>
        <Cell>
          <div className={styles.hotelName}>
            <span className={styles.brandName}>{brand}</span>
            <span className={styles.name}>
              <span className={styles.id}>#{hotel.displayId} </span>
              {shortName}
            </span>
          </div>
        </Cell>

        <Cell>
          <div className={styles.groupName}>
            <span className={styles.merchantName}>{hotel.merchantName}</span>
          </div>
        </Cell>

        <Cell>
          <div className={styles.brandName}>
            <span className={styles.parentChainCode}>{hotel.parentChainCode}</span>
            <span className={styles.chainCode}>{hotel.chainCode}</span>
          </div>
        </Cell>

        <Cell>
          <div className={styles.csmInitials}>
            <span>
              {hotel.csmUser?.fullName
                ?.split(' ')
                .map(str => str[0])
                .join('.')}
            </span>
          </div>
        </Cell>

        <Cell data-tip={moment(hotel.addedOn).format()} data-for="hotels-tooltip">
          <div className={styles.addedOn}>
            <span className={styles.date}>
              {moment(hotel.addedOn).format('MMM DD, YYYY')}
            </span>
            <span className={styles.owner}>{hotel.addedBy}</span>
          </div>
        </Cell>

        <Cell data-tip={moment(hotel.updatedOn).format()} data-for="hotels-tooltip">
          <div className={styles.updatedOn}>
            <span className={styles.date}>
              {moment(hotel.updatedOn).format('MMM DD, YYYY')}
            </span>
            <span className={styles.owner}>{hotel.updatedBy}</span>
          </div>
        </Cell>

        {isHealthyHotel ? (
          <Cell>
            <div className={styles.tags}>
              {tags.map((tag, index) => (
                <span key={index} className={styles.tag}>
                  {tag}
                </span>
              ))}

              {hotel.tags.length > 2 && (
                <p className={styles.moreText}>+{hotel.tags.length - 2}</p>
              )}
            </div>
          </Cell>
        ) : (
          <Cell>
            <div className={styles.status}>
              <span className={styles.statusText}>{getStatusText(hotel)}</span>

              {!isOnboarding && (
                <span className={styles.issueText}>{getIssueText(hotel)}</span>
              )}
            </div>
          </Cell>
        )}

        <Cell>
          <div className={styles.ctaCol}>
            {isHealthyHotel ? (
              <button
                disabled={hotel.isDeleted}
                className={styles.cta}
                onClick={handleHotelClick}
              >
                View
              </button>
            ) : (
              <button
                disabled={hotel.isDeleted}
                className={styles.brokenCta}
                onClick={handleHotelClick}
              >
                {getBrokenCtaText(hotel)}
              </button>
            )}
            {props.category === 'onboarding' ||
            props.category === 'needsAttention' ||
            props.category === 'followUp' ? (
              <button
                className={styles.remindLaterButton}
                onClick={() => setIsRemindLaterDialogVisible(true)}
                title={
                  hotel.remindLater?.afterOn
                    ? `${hotel.remindLater.updatedBy} added this on ${moment(
                        hotel.remindLater.addedOn
                      ).format('MMM DD, YYYY')} with note: "${hotel.remindLater.reason}"`
                    : null
                }
                data-tip={
                  hotel.remindLater?.afterOn
                    ? `${hotel.remindLater.updatedBy} added this on ${moment(
                        hotel.remindLater.addedOn
                      ).format('MMM DD, YYYY')} with note: "${hotel.remindLater.reason}"`
                    : null
                }
              >
                <ClockIcon width={16} />
              </button>
            ) : null}
          </div>
        </Cell>
      </Row>
    </div>
  )
}
